import { GraphQLError } from "graphql";
import { ClientError } from "graphql-request";

import toast from "components/ui/toast/Toast";

const CONFLICT = 409;
const FORBIDDEN = 403;

/**
 * The message from an error app-service raised for the user, such as a content library name
 * clash. Defaults to conflicts; pass other statuses whose messages are written for customers.
 *
 * Note the status lives under `originalError` for a plain NestJS HttpException, while the
 * permission errors below carry theirs at the top level.
 */
export function getUserFacingMessage(
  error: unknown,
  statuses: number[] = [CONFLICT],
): string | undefined {
  if (!(error instanceof ClientError)) {
    return undefined;
  }

  return error.response?.errors?.find((graphQlError) => {
    const originalError: unknown = graphQlError.extensions?.originalError;

    return (
      typeof originalError === "object" &&
      originalError !== null &&
      "statusCode" in originalError &&
      typeof originalError.statusCode === "number" &&
      statuses.includes(originalError.statusCode)
    );
  })?.message;
}

/** Tells the user which permission check turned them away, if that's why the call failed. */
export function handlePermissionError(error: ClientError) {
  const permissionError = error.response?.errors?.find(
    (e: GraphQLError) => e.extensions?.statusCode === FORBIDDEN && e.extensions?.permissionCheck,
  );

  if (permissionError && permissionError.extensions?.permissionCheck) {
    const messageFromReason = getPermissionErrorMessage(
      permissionError.extensions.permissionCheck.toString(),
    );
    toast.error(messageFromReason, {
      id: "permission-error", // ensures only one toast is shown
      duration: 10000,
    });
  }
}

function getPermissionErrorMessage(permission: string): string {
  switch (permission) {
    case "checkDataSharing":
      return "This company has not enabled data sharing access with admins";
    case "requiredDataTypes":
      return "Missing required data type access";
    case "inCompany":
      return "Current company does not have access";
    case "isAdmin":
      return "Admin access required";
    case "isContractor":
      return "Contractor access required";
    default:
      return "Forbidden resource";
  }
}
